import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Car, MapPin, Fuel, Calendar, Gauge, ChevronLeft, ChevronRight, X, MessageCircle, Mail, Phone, ArrowLeft, BookOpen } from "lucide-react";
import { Footer } from "../components/Footer";
import { toast } from "sonner";

const envBackend = process.env.REACT_APP_BACKEND_URL;
const API = envBackend ? `${envBackend}/api` : (window.APP_CONFIG?.API_URL || '/api');

const STATUS_LABELS = {
  disponible: { text: "Disponible", class: "bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30" },
  en_proceso: { text: "En proceso", class: "bg-[#007AFF]/20 text-[#007AFF] border-[#007AFF]/30" },
  reservado: { text: "Reservado", class: "bg-[#FF9500]/20 text-[#FF9500] border-[#FF9500]/30" },
  vendido: { text: "Vendido", class: "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30" },
};

const formatPrice = (n) => n?.toLocaleString("es-ES") + " €";

function VehicleCard({ vehicle, onSelect }) {
  const firstImage = vehicle.images?.[0];
  const status = STATUS_LABELS[vehicle.status] || STATUS_LABELS.disponible;

  return (
    <div
      className="glass-card overflow-hidden cursor-pointer hover:border-[#FF3B30]/40 transition-all duration-300 group"
      onClick={() => onSelect(vehicle)}
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      <div className="relative aspect-[16/10] bg-[#111] overflow-hidden">
        {firstImage ? (
          <img src={`${API}/images/${firstImage}`} alt={vehicle.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#636366]"><Car className="w-12 h-12" /></div>
        )}
        <span className={`absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full border ${status.class}`}>{status.text}</span>
      </div>
      <div className="p-4">
        <h3 className="text-white font-bold text-base truncate">{vehicle.title || `${vehicle.make} ${vehicle.model}`}</h3>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-[#8A8A8D]">
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{vehicle.year}</span>
          <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{vehicle.kilometers?.toLocaleString()} km</span>
          <span className="flex items-center gap-1"><Fuel className="w-3 h-3" />{vehicle.fuel_type}</span>
          {vehicle.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{vehicle.location}</span>}
        </div>
        <p className="text-[#FF3B30] text-xl font-bold mt-3">{formatPrice(vehicle.price)}</p>
      </div>
    </div>
  );
}

function VehicleDetail({ vehicle, onClose }) {
  const [currentImg, setCurrentImg] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", phone: "", email: "", message: `Hola, estoy interesado en el ${vehicle.title || vehicle.make + ' ' + vehicle.model}` });
  const images = vehicle.images || [];
  const status = STATUS_LABELS[vehicle.status] || STATUS_LABELS.disponible;

  const whatsappMsg = encodeURIComponent(`Hola, me interesa el ${vehicle.title || vehicle.make + ' ' + vehicle.model} (${vehicle.year}) que tenéis en telobajo.com por ${formatPrice(vehicle.price)}`);
  const whatsappUrl = `https://wa.me/34638475651?text=${whatsappMsg}`;

  const handleContactSubmit = async () => {
    if (!contactForm.name || !contactForm.phone) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }
    try {
      await axios.post(`${API}/contact`, {
        name: contactForm.name,
        email: contactForm.email || "no indicado",
        message: `[MARKETPLACE - ${vehicle.title || vehicle.make + ' ' + vehicle.model}] Tel: ${contactForm.phone}. ${contactForm.message}`
      });
      toast.success("Mensaje enviado. Te contactaremos pronto.");
      setShowContact(false);
    } catch {
      toast.error("Error al enviar. Inténtalo de nuevo.");
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="glass-card border-[rgba(138,138,141,0.2)] sm:max-w-2xl">
          <DialogHeader className="pb-1">
            <DialogTitle className="text-base sm:text-lg font-bold text-white flex items-center gap-2 pr-6">
              {vehicle.title || `${vehicle.make} ${vehicle.model}`}
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${status.class} ml-auto flex-shrink-0`}>{status.text}</span>
            </DialogTitle>
          </DialogHeader>

          {/* Image gallery */}
          {images.length > 0 && (
            <div className="relative aspect-[16/10] bg-[#111] rounded-lg overflow-hidden">
              <img src={`${API}/images/${images[currentImg]}`} alt="" className="w-full h-full object-cover" />
              {images.length > 1 && (
                <>
                  <button onClick={() => setCurrentImg(i => (i - 1 + images.length) % images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-1.5 text-white hover:bg-black/80"><ChevronLeft className="w-5 h-5" /></button>
                  <button onClick={() => setCurrentImg(i => (i + 1) % images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-1.5 text-white hover:bg-black/80"><ChevronRight className="w-5 h-5" /></button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button key={i} onClick={() => setCurrentImg(i)} className={`w-2 h-2 rounded-full ${i === currentImg ? 'bg-white' : 'bg-white/40'}`} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Details */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="bg-[#111] rounded-lg p-2.5 text-center"><Calendar className="w-4 h-4 mx-auto mb-1 text-[#FF3B30]" /><span className="text-[#8A8A8D]">Año</span><p className="text-white font-bold">{vehicle.year}</p></div>
            <div className="bg-[#111] rounded-lg p-2.5 text-center"><Gauge className="w-4 h-4 mx-auto mb-1 text-[#FF3B30]" /><span className="text-[#8A8A8D]">Km</span><p className="text-white font-bold">{vehicle.kilometers?.toLocaleString()}</p></div>
            <div className="bg-[#111] rounded-lg p-2.5 text-center"><Fuel className="w-4 h-4 mx-auto mb-1 text-[#FF3B30]" /><span className="text-[#8A8A8D]">Combustible</span><p className="text-white font-bold">{vehicle.fuel_type}</p></div>
            <div className="bg-[#111] rounded-lg p-2.5 text-center"><Car className="w-4 h-4 mx-auto mb-1 text-[#FF3B30]" /><span className="text-[#8A8A8D]">Potencia</span><p className="text-white font-bold">{vehicle.power_cv} CV</p></div>
          </div>

          {vehicle.color && <p className="text-xs text-[#8A8A8D]">Color: <span className="text-white">{vehicle.color}</span></p>}
          {vehicle.location && <p className="text-xs text-[#8A8A8D]">Ubicación: <span className="text-white">{vehicle.location}</span></p>}
          {vehicle.notes && <p className="text-xs text-[#C4C4C6] bg-[#111] rounded-lg p-3">{vehicle.notes}</p>}

          <div className="flex items-center justify-between mt-1">
            <p className="text-[#FF3B30] text-2xl font-bold">{formatPrice(vehicle.price)}</p>
            <span className="text-xs text-[#8A8A8D]">Entrega inmediata</span>
          </div>

          <div className="flex gap-2">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid="whatsapp-button">
              <Button className="w-full h-11 bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm">
                <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
              </Button>
            </a>
            <Button onClick={() => setShowContact(true)} className="flex-1 h-11 btn-red text-sm" data-testid="contact-vehicle-button">
              <Mail className="w-4 h-4 mr-2" />Contactar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact sub-dialog */}
      <Dialog open={showContact} onOpenChange={setShowContact}>
        <DialogContent className="glass-card border-[rgba(138,138,141,0.2)]">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Contactar por {vehicle.title || vehicle.make}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[#C4C4C6] text-xs">Nombre *</Label><Input value={contactForm.name} onChange={e => setContactForm(f => ({...f, name: e.target.value}))} className="glass-input h-10 mt-1" placeholder="Tu nombre" data-testid="contact-name" /></div>
            <div><Label className="text-[#C4C4C6] text-xs">Teléfono *</Label><Input value={contactForm.phone} onChange={e => setContactForm(f => ({...f, phone: e.target.value}))} className="glass-input h-10 mt-1" placeholder="+34 600 000 000" data-testid="contact-phone" /></div>
            <div><Label className="text-[#C4C4C6] text-xs">Email</Label><Input value={contactForm.email} onChange={e => setContactForm(f => ({...f, email: e.target.value}))} className="glass-input h-10 mt-1" placeholder="tu@email.com" data-testid="contact-email" /></div>
            <div><Label className="text-[#C4C4C6] text-xs">Mensaje</Label><textarea value={contactForm.message} onChange={e => setContactForm(f => ({...f, message: e.target.value}))} className="glass-input w-full h-20 p-3 mt-1 rounded-md text-sm text-white resize-none" data-testid="contact-message" /></div>
            <Button onClick={handleContactSubmit} className="btn-red w-full h-11" data-testid="contact-submit">Enviar mensaje</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function MarketplacePage() {
  const [vehicles, setVehicles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/vehicles`).then(res => {
      setVehicles(res.data.vehicles);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] bg-carbon-pattern">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-[rgba(138,138,141,0.1)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="/logo-telobajo-new.png" alt="Te Lo Bajo" className="h-14 sm:h-16 w-auto" />
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link to="/importacion" className="flex items-center gap-1.5 bg-[#FF3B30] hover:bg-[#E0352B] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-importacion">
              <span className="hidden sm:inline">Importación</span><span className="sm:hidden">Import</span>
            </Link>
            <Link to="/ocasion" className="flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#006AE0] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-nacionales">
              <span className="hidden sm:inline">Nacionales</span><span className="sm:hidden">Nacional</span>
            </Link>
            <Link to="/coches-disponibles" className="flex items-center gap-1.5 bg-[#34C759] hover:bg-[#2DB84E] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-stock">
              <Car className="w-4 h-4" /><span className="hidden sm:inline">Stock</span><span className="sm:hidden">Stock</span>
            </Link>
            <Link to="/blog" className="flex items-center gap-1.5 bg-[#F5A623] hover:bg-[#D4901E] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-blog">
              <BookOpen className="w-4 h-4" /><span className="hidden sm:inline">Blog</span><span className="sm:hidden">Blog</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="relative pt-24 pb-8 px-4 sm:px-6">
        <div className="absolute inset-0 bg-cover bg-bottom bg-no-repeat" style={{ backgroundImage: "url('/hero-bg.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/90 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3" data-testid="marketplace-title">
            Coches <span className="text-[#FF3B30]">disponibles</span>
          </h1>
          <p className="text-[#8A8A8D] text-sm sm:text-base max-w-xl mx-auto">
            Vehículos ya importados, revisados y listos para entrega inmediata
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="px-4 sm:px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center text-[#8A8A8D] py-20">Cargando vehículos...</div>
          ) : vehicles.length === 0 ? (
            <div className="text-center py-20">
              <Car className="w-16 h-16 text-[#636366] mx-auto mb-4" />
              <p className="text-[#8A8A8D] text-lg">No hay vehículos disponibles en este momento</p>
              <p className="text-[#636366] text-sm mt-2">Vuelve pronto o calcula el coste de importar el tuyo</p>
              <Link to="/importacion"><Button className="btn-red mt-6">Calcular importación</Button></Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vehicles-grid">
              {vehicles.map(v => <VehicleCard key={v.id} vehicle={v} onSelect={setSelected} />)}
            </div>
          )}
        </div>
      </section>

      {selected && <VehicleDetail vehicle={selected} onClose={() => setSelected(null)} />}
      <Footer />
    </div>
  );
}
